import { useMemo } from "react";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import * as client from "../../../client";
import {
  CatResultsResponse,
  PerilModelProfile,
} from "../../../client/types.gen";
import { RmsVersionBadge } from "./RmsVersionBadge";
import { formatExactMoneyDollars } from "../Money";
import { useFeatureEnabled } from "../../../IfFeatureEnabled";
import { useLocalUser } from "../data";
import { compareLayers, readableLayer } from "../layers/util";
import { getPerilColor } from "../icm/util";
import {
  SelectedModel,
  RmsLayerResult,
  LayerPerilRow,
  getPerilDisplayName,
  isLayerTotalPeril,
} from "./types";
import styles from "./CatCard.module.css";

interface LayerPerilTabProps {
  selectedModel: SelectedModel;
  rmsEltResultsData: CatResultsResponse | null;
  rms22ResultsData: CatResultsResponse | null;
  icmYltResultsData: CatResultsResponse | null;
  spatialKatResultsData: CatResultsResponse | null;
  layerInfoById: Map<string, client.Layer>;
  showAllLayers: boolean;
  onShowAllLayersChange: (value: boolean) => void;
  // When false, the parent renders the "Show all layers" toggle itself.
  renderToggle: boolean;
  // Roof source that badges each peril "Enhanced · <source>"; absent = broker data.
  enhancedSource?: string | null;
}

const compareByResolvedLayer =
  (layerInfoById: Map<string, client.Layer>) =>
  <T extends { layer_id: string | null }>(a: T, b: T): number => {
    const infoA = a.layer_id ? layerInfoById.get(a.layer_id) : undefined;
    const infoB = b.layer_id ? layerInfoById.get(b.layer_id) : undefined;
    if (!infoA) return infoB ? 1 : 0;
    if (!infoB) return -1;
    return compareLayers(infoA, infoB);
  };

export const LayerPerilTab: React.FC<LayerPerilTabProps> = ({
  selectedModel,
  rmsEltResultsData,
  rms22ResultsData,
  icmYltResultsData,
  spatialKatResultsData,
  layerInfoById,
  showAllLayers,
  onShowAllLayersChange,
  renderToggle,
  enhancedSource,
}) => {
  const isLayerVisible = (layerId: string | null | undefined): boolean => {
    if (showAllLayers) return true;
    if (!layerId) return true;
    const info = layerInfoById.get(layerId);
    return info?.is_enabled !== false;
  };

  const getRmsEltLayers = useMemo((): RmsLayerResult[] => {
    const eltData = rmsEltResultsData?.rms_elt?.aggregate;
    if (!eltData?.layers) return [];

    return eltData.layers
      .filter((layer) => layer.layer_guid && layer.layer_guid !== "")
      .map((layer) => ({
        ...layer,
        layer_id: layer.layer_guid,
      }));
  }, [rmsEltResultsData]);

  const getRms22Layers = useMemo((): RmsLayerResult[] => {
    const data = rms22ResultsData?.rms_22?.aggregate;
    if (!data?.layers) return [];

    return data.layers
      .filter((layer) => layer.layer_guid && layer.layer_guid !== "")
      .map((layer) => ({
        ...layer,
        layer_id: layer.layer_guid,
      }));
  }, [rms22ResultsData]);

  const getIcmYltLayers = useMemo((): RmsLayerResult[] => {
    const yltData = icmYltResultsData?.icm_ylt?.aggregate;
    if (!yltData?.layers) return [];

    return yltData.layers
      .filter((layer) => layer.layer_guid && layer.layer_guid !== "")
      .map((layer) => ({
        ...layer,
        layer_id: layer.layer_guid,
      }));
  }, [icmYltResultsData]);

  const getSpatialKatLayers = useMemo((): RmsLayerResult[] => {
    const skData = spatialKatResultsData?.spatialkat;
    if (!skData?.aggregate?.layers) return [];

    return skData.aggregate.layers
      .filter((layer) => layer.layer_guid && layer.layer_guid !== "")
      .map((layer) => ({
        ...layer,
        layer_id: layer.layer_guid,
      }));
  }, [spatialKatResultsData]);

  // RMS model version per peril code, for the version badge. Only the RMS runs
  // report it; other models and older runs leave it empty (no badge).
  const modelProfileByPeril = useMemo(() => {
    const profiles =
      selectedModel === "rms_elt"
        ? rmsEltResultsData?.rms_elt?.model_profiles
        : selectedModel === "rms_22"
          ? rms22ResultsData?.rms_22?.model_profiles
          : undefined;
    const byPeril = new Map<string, PerilModelProfile>();
    for (const profile of profiles ?? []) {
      byPeril.set(profile.peril, profile);
    }
    return byPeril;
  }, [selectedModel, rmsEltResultsData, rms22ResultsData]);

  const renderPerilLabel = (perilCode: string) => {
    if (!perilCode) {
      return null;
    }
    const label = getPerilDisplayName(perilCode);
    if (isLayerTotalPeril(perilCode)) {
      return <span className={styles.fontBold}>{label}</span>;
    }

    return (
      <span className={styles.perilLabel}>
        <span
          className={styles.perilColorDot}
          style={{ backgroundColor: getPerilColor(perilCode) }}
        />
        <span>{label}</span>
        <RmsVersionBadge
          profile={modelProfileByPeril.get(perilCode)}
          enhancedSource={enhancedSource}
        />
      </span>
    );
  };

  // Persisted job-time PMI — ICM only, at 100% of each enabled layer
  // (gross, matching the PML columns; pinned against participation edits).
  // Per-peril figures repeat across that peril's layer rows; the Combined
  // row shows no PMI. Interactive what-ifs live on the Layers tab.
  const user = useLocalUser();
  const pmiEnabled = useFeatureEnabled(
    "portfolio-marginal-impact",
    undefined,
    user
  );
  const pmi =
    pmiEnabled && selectedModel === "icm_ylt"
      ? (icmYltResultsData?.icm_ylt?.aggregate?.pmi ?? null)
      : null;

  const lossesByLayerPeril = useMemo((): LayerPerilRow[] => {
    const modelLayers =
      selectedModel === "spatialkat"
        ? getSpatialKatLayers
        : selectedModel === "rms_elt"
          ? getRmsEltLayers
          : selectedModel === "rms_22"
            ? getRms22Layers
            : selectedModel === "icm_ylt"
              ? getIcmYltLayers
              : [];
    if (modelLayers.length === 0) return [];

    const filteredLayers = modelLayers.filter((layer) =>
      isLayerVisible(layer.layer_id)
    );

    const sortedLayers = [...filteredLayers].sort(
      compareByResolvedLayer(layerInfoById)
    );

    const rows: LayerPerilRow[] = [];

    for (const rmsLayer of sortedLayers) {
      const layerInfo = rmsLayer.layer_id
        ? layerInfoById.get(rmsLayer.layer_id)
        : undefined;
      const layerName = layerInfo
        ? readableLayer(layerInfo)
        : (rmsLayer.layer_id ?? "Unknown");

      // RMS perils are in the perils object, but they may not have AAL values
      // For now, just show the totals
      const perilEntries = Object.entries(rmsLayer.perils || {}).sort(
        ([a], [b]) =>
          getPerilDisplayName(a).localeCompare(getPerilDisplayName(b))
      );

      // Get EP curves for this layer for PML values
      const layerEpCurves = rmsLayer.ep_curves || [];
      const grAepCurve = layerEpCurves.find(
        (c: client.EpRow) => c.perspective === "GR" && c.metric_type === "AEP"
      );
      const pml100 = grAepCurve?.return_period_values?.["100"] ?? null;
      const pml250 = grAepCurve?.return_period_values?.["250"] ?? null;
      const pml1000 = grAepCurve?.return_period_values?.["1000"] ?? null;

      let isFirst = true;
      for (const [peril, data] of perilEntries) {
        const perilData = data as client.CatPerilLayerData;
        const perilPmi = pmi?.per_peril?.[peril];
        rows.push({
          layer: layerName,
          peril,
          aal: perilData.aal ?? null,
          pml_100: perilData.pml_per_period?.["100"] ?? null,
          pml_250: perilData.pml_per_period?.["250"] ?? null,
          pml_1000: perilData.pml_per_period?.["1000"] ?? null,
          pmi_100: perilPmi?.["100"] ?? null,
          pmi_250: perilPmi?.["250"] ?? null,
          pmi_1000: perilPmi?.["1000"] ?? null,
          isFirstInGroup: isFirst,
        });
        isFirst = false;
      }

      // Add combined row from totals
      rows.push({
        layer: layerName,
        peril: "Combined",
        aal: rmsLayer.totals?.aal ?? null,
        pml_100: pml100,
        pml_250: pml250,
        pml_1000: pml1000,
        // No all-peril PMI (undocumented cross-peril year alignment).
        pmi_100: null,
        pmi_250: null,
        pmi_1000: null,
        isFirstInGroup: isFirst,
      });
    }

    return rows;
  }, [
    pmi,
    selectedModel,
    rmsEltResultsData,
    rms22ResultsData,
    icmYltResultsData,
    spatialKatResultsData,
    layerInfoById,
    getRmsEltLayers,
    getRms22Layers,
    getIcmYltLayers,
    getSpatialKatLayers,
    showAllLayers,
  ]);

  const pmiHeader = (label: string) => (
    <span
      title={
        "Portfolio Marginal Impact at 100% participation (gross, matching " +
        "the AAL and PML columns; participation edits never move this " +
        "figure - use a layer's Portfolio impact view for share-scaled " +
        "figures). Account-wide per peril; PMIs do not sum across perils " +
        "or layers."
      }
    >
      {label}
    </span>
  );

  const pmiCell =
    (field: "pmi_100" | "pmi_250" | "pmi_1000") => (rowData: LayerPerilRow) => {
      const value = rowData[field];
      return value != null
        ? formatExactMoneyDollars(value, {
            withRounding: true,
            withDollarSign: true,
          })
        : "\u2014";
    };

  const toggle = renderToggle ? (
    <div className={styles.layerFilterToggle}>
      <Checkbox
        inputId="show-all-layers"
        checked={showAllLayers}
        onChange={(e) => onShowAllLayersChange(!!e.checked)}
      />
      <label htmlFor="show-all-layers">Show all layers</label>
    </div>
  ) : null;

  if (lossesByLayerPeril.length === 0) {
    return (
      <>
        {toggle}
        <Message
          severity="info"
          text={
            showAllLayers
              ? `No ${selectedModel === "icm_ylt" ? "ICM" : selectedModel === "spatialkat" ? "KatRisk" : selectedModel === "rms_22" ? "RMS 22" : "RMS"} data available. Run an analysis to generate results.`
              : "No enabled layers to display. Toggle 'Show all layers' to see disabled layers."
          }
          className={styles.messageMargin}
        />
      </>
    );
  }

  return (
    <>
      {toggle}
      <DataTable
        value={lossesByLayerPeril}
        rowGroupMode="rowspan"
        groupRowsBy="layer"
        rowClassName={(rowData: LayerPerilRow) => {
          const classes: string[] = [];
          if (isLayerTotalPeril(rowData.peril)) {
            classes.push(styles.layerTotalRow);
          }
          if (rowData.isFirstInGroup) {
            classes.push(styles.layerGroupStart);
          }
          return classes.join(" ");
        }}
      >
        <Column
          field="layer"
          header="Layer"
          style={{ minWidth: "100px" }}
          bodyClassName={styles.layerCell}
        />
        <Column
          field="peril"
          header="Peril"
          body={(rowData: LayerPerilRow) => renderPerilLabel(rowData.peril)}
          style={{ minWidth: "100px" }}
        />
        <Column
          field="aal"
          header="AAL"
          body={(rowData: LayerPerilRow) =>
            rowData.aal != null
              ? formatExactMoneyDollars(rowData.aal, {
                  withRounding: true,
                  withDollarSign: true,
                })
              : ""
          }
          style={{ minWidth: "120px" }}
          align="right"
        />
        <Column
          field="pml_100"
          header="100 Year PML"
          body={(rowData: LayerPerilRow) =>
            rowData.pml_100 != null
              ? formatExactMoneyDollars(rowData.pml_100, {
                  withRounding: true,
                  withDollarSign: true,
                })
              : ""
          }
          style={{ minWidth: "120px" }}
          align="right"
          headerClassName={styles.pmlColumn}
          bodyClassName={styles.pmlColumn}
        />
        <Column
          field="pml_250"
          header="250 Year PML"
          body={(rowData: LayerPerilRow) =>
            rowData.pml_250 != null
              ? formatExactMoneyDollars(rowData.pml_250, {
                  withRounding: true,
                  withDollarSign: true,
                })
              : ""
          }
          style={{ minWidth: "120px" }}
          align="right"
          headerClassName={styles.pmlColumn}
          bodyClassName={styles.pmlColumn}
        />
        <Column
          field="pml_1000"
          header="1000 Year PML"
          body={(rowData: LayerPerilRow) =>
            rowData.pml_1000 != null
              ? formatExactMoneyDollars(rowData.pml_1000, {
                  withRounding: true,
                  withDollarSign: true,
                })
              : ""
          }
          style={{ minWidth: "120px" }}
          align="right"
          headerClassName={styles.pmlColumn}
          bodyClassName={styles.pmlColumn}
        />
        {pmi && (
          <Column
            field="pmi_100"
            header={pmiHeader("100 Year PMI")}
            body={pmiCell("pmi_100")}
            style={{ minWidth: "120px" }}
            align="right"
            headerClassName={styles.pmlColumn}
            bodyClassName={styles.pmlColumn}
          />
        )}
        {pmi && (
          <Column
            field="pmi_250"
            header={pmiHeader("250 Year PMI")}
            body={pmiCell("pmi_250")}
            style={{ minWidth: "120px" }}
            align="right"
            headerClassName={styles.pmlColumn}
            bodyClassName={styles.pmlColumn}
          />
        )}
        {pmi && (
          <Column
            field="pmi_1000"
            header={pmiHeader("1000 Year PMI")}
            body={pmiCell("pmi_1000")}
            style={{ minWidth: "120px" }}
            align="right"
            headerClassName={styles.pmlColumn}
            bodyClassName={styles.pmlColumn}
          />
        )}
      </DataTable>
      {pmi && (
        <div className={styles.pmiFootnote}>
          All AALs, PMLs, and PMIs assume 100% participation (gross basis). PMI
          (Portfolio Marginal Impact): this account's contribution to the
          combined book's PML. Pinned - participation edits never change it;
          each layer's Portfolio impact view on the Layers tab shows the
          share-scaled figure. Not additive across rows. Book as of{" "}
          {new Date(pmi.as_of).toLocaleString()} · {pmi.portfolio_report_count}{" "}
          in-force account{pmi.portfolio_report_count === 1 ? "" : "s"}.
        </div>
      )}
    </>
  );
};
